import React from 'react';
import { Controller, Control } from 'react-hook-form';
import { Grid, TextField, Typography, Box } from '@mui/material';
import { LactateFormValues } from '../../formModel';
import { Modality } from '../../types';
import { unitsForModality, THRESHOLD_META } from '../../constants';

interface Props {
  control: Control<LactateFormValues>;
  modality: Modality;
  errors: Record<string, string>;
}

export const ThresholdSection: React.FC<Props> = ({ control, modality, errors }) => {
  const { intensityLabel, intensityUnit, showPace } = unitsForModality(modality);
  const keys: ('t1' | 't2')[] = ['t1', 't2'];

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Thresholds
      </Typography>
      <Grid container spacing={3}>
        {keys.map((key) => (
          <Grid item xs={12} md={6} key={key}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', p: 2 }}>
              <Typography variant="subtitle1" sx={{ color: THRESHOLD_META[key].color, fontWeight: 700 }}>
                {THRESHOLD_META[key].name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                {THRESHOLD_META[key].subInitial}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Controller
                    name={`thresholds.${key}.intensity` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${intensityLabel} (${intensityUnit})`}
                        fullWidth
                        size="small"
                        error={Boolean(errors[`thresholds.${key}.intensity`])}
                        helperText={errors[`thresholds.${key}.intensity`]}
                      />
                    )}
                  />
                </Grid>
                {showPace && (
                  <Grid item xs={6}>
                    <Controller
                      name={`thresholds.${key}.pace` as const}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pace (min/km)"
                          placeholder="mm:ss"
                          fullWidth
                          size="small"
                          error={Boolean(errors[`thresholds.${key}.pace`])}
                          helperText={errors[`thresholds.${key}.pace`]}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Controller
                    name={`thresholds.${key}.hr` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="HR (bpm)"
                        fullWidth
                        size="small"
                        error={Boolean(errors[`thresholds.${key}.hr`])}
                        helperText={errors[`thresholds.${key}.hr`]}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name={`thresholds.${key}.hr_pct_max` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="% HRmax"
                        fullWidth
                        size="small"
                        error={Boolean(errors[`thresholds.${key}.hr_pct_max`])}
                        helperText={errors[`thresholds.${key}.hr_pct_max`]}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  );
};
